/**
 * RBAC catalog — Zod schemas for the static role/permission
 * metadata the backend exposes at GET /api/v1/rbac/permissions.
 *
 * Consumed by the admin invite dialog so the role selector can
 * filter by the inviter's rank and the permission matrix on the
 * right column renders the actual permissions the role grants.
 */

import { z } from "zod";

export const rbacRoleSchema = z.enum([
  "platform_admin",
  "platform_operator",
  "tenant_admin",
  "tenant_operator",
  "tenant_readonly",
  "org_admin",
  "org_operator",
  // Service role for the agent-governance reconciliation worker (minted as an
  // API key, not a human invite). It IS part of the backend RBAC catalog, so it
  // MUST be listed here or the catalog GET fails to parse and the whole invite
  // page breaks. The invite dialog's INVITABLE_ROLE_SET keeps it out of the
  // human role selector. Keep in sync with internal/admin/rbac/rbac.go.
  "agent_reconciler",
  // The seat given to a visiting assessor: compliance.read + audit.read and
  // nothing else. Same rule as agent_reconciler above — it is in the backend
  // catalog, so it MUST be here or the catalog GET fails to parse and the
  // invite page never mounts. It IS invitable, unlike agent_reconciler.
  "compliance_reader",
]);
export type RBACRole = z.infer<typeof rbacRoleSchema>;

export const rbacScopeSchema = z.enum(["platform", "tenant", "organization"]);
export type RBACScope = z.infer<typeof rbacScopeSchema>;

export const roleCatalogEntrySchema = z.object({
  role: rbacRoleSchema,
  scope: rbacScopeSchema,
  display_name: z.string(),
  description: z.string(),
  rank: z.number().int(),
  permissions: z.array(z.string()),
});
export type RoleCatalogEntry = z.infer<typeof roleCatalogEntrySchema>;

export const permissionCatalogEntrySchema = z.object({
  permission: z.string(),
  scope: rbacScopeSchema,
  description: z.string(),
  step_up_required: z.boolean(),
});
export type PermissionCatalogEntry = z.infer<typeof permissionCatalogEntrySchema>;

export const rbacCatalogSchema = z.object({
  roles: z.array(roleCatalogEntrySchema),
  permissions: z.array(permissionCatalogEntrySchema),
});
export type RBACCatalog = z.infer<typeof rbacCatalogSchema>;
